/**
 * Glass Panel Factory — creates frosted-glass DOM elements
 * with consistent styling from the Liquid Glass design tokens.
 */

export type GlassElevation = 'base' | 'elevated' | 'solid'

export interface GlassPanelOptions {
  /** Tag name for the element. Default: 'div' */
  tag?: string
  /** Corner radius token. Default: 'md' */
  radius?: 'sm' | 'md' | 'lg' | 'pill'
  /** Glass elevation / opacity level. Default: 'base' */
  elevation?: GlassElevation
  /** Whether to add the hover border-brighten effect. Default: false */
  interactive?: boolean
  /** Extra CSS class names. */
  className?: string
  /** ARIA role. */
  role?: string
}

const RADIUS_MAP = {
  sm: 'var(--hs-radius-sm)',
  md: 'var(--hs-radius-md)',
  lg: 'var(--hs-radius-lg)',
  pill: 'var(--hs-radius-pill)',
}

const BG_MAP: Record<GlassElevation, string> = {
  base: 'var(--hs-glass-bg)',
  elevated: 'var(--hs-glass-bg-elevated)',
  solid: 'var(--hs-glass-bg-solid)',
}

/**
 * Create a glass panel element with liquid glass styling applied.
 * All visual properties use CSS custom properties so they respond
 * to theme changes (dark ↔ light).
 */
export function createGlassPanel(options: GlassPanelOptions = {}): HTMLElement {
  const {
    tag = 'div',
    radius = 'md',
    elevation = 'base',
    interactive = false,
    className,
    role,
  } = options

  const el = document.createElement(tag)
  if (className) el.className = className
  if (role) el.setAttribute('role', role)

  Object.assign(el.style, {
    background: BG_MAP[elevation],
    backdropFilter: `blur(var(--hs-glass-blur)) saturate(var(--hs-glass-saturate))`,
    WebkitBackdropFilter: `blur(var(--hs-glass-blur)) saturate(var(--hs-glass-saturate))`,
    border: '1px solid var(--hs-glass-border)',
    borderRadius: RADIUS_MAP[radius],
    boxShadow: elevation === 'elevated'
      ? 'var(--hs-glass-shadow-elevated)'
      : 'var(--hs-glass-shadow)',
    color: 'var(--hs-text-primary)',
    fontFamily: 'var(--hs-font)',
    fontSize: 'var(--hs-text-base)',
    lineHeight: 'var(--hs-line-height)',
    boxSizing: 'border-box',
  })

  if (interactive) {
    el.style.transition = `border-color var(--hs-fade-duration) var(--hs-ease-fast),
                           box-shadow var(--hs-fade-duration) var(--hs-ease-fast)`
    el.style.cursor = 'pointer'
    el.addEventListener('mouseenter', () => {
      el.style.borderColor = 'var(--hs-glass-border-hover)'
    })
    el.addEventListener('mouseleave', () => {
      el.style.borderColor = 'var(--hs-glass-border)'
    })
  }

  return el
}

/** Apply glass text styling to an element. */
export function glassText(
  el: HTMLElement,
  variant: 'primary' | 'secondary' | 'tertiary' = 'primary',
  size: 'xs' | 'sm' | 'base' | 'lg' | 'xl' = 'base',
  weight: 'normal' | 'medium' | 'semibold' = 'normal',
): HTMLElement {
  const colorMap = {
    primary: 'var(--hs-text-primary)',
    secondary: 'var(--hs-text-secondary)',
    tertiary: 'var(--hs-text-tertiary)',
  }
  const sizeMap = {
    xs: 'var(--hs-text-xs)',
    sm: 'var(--hs-text-sm)',
    base: 'var(--hs-text-base)',
    lg: 'var(--hs-text-lg)',
    xl: 'var(--hs-text-xl)',
  }
  const weightMap = {
    normal: 'var(--hs-weight-normal)',
    medium: 'var(--hs-weight-medium)',
    semibold: 'var(--hs-weight-semibold)',
  }

  Object.assign(el.style, {
    color: colorMap[variant],
    fontSize: sizeMap[size],
    fontWeight: weightMap[weight],
    fontFamily: 'var(--hs-font)',
    lineHeight: 'var(--hs-line-height)',
    margin: '0',
  })

  return el
}

/** Create a glass-styled button. */
export function createGlassButton(options: {
  label: string
  variant?: 'default' | 'accent' | 'ghost'
  size?: 'sm' | 'md'
  icon?: string
  onClick?: (e: MouseEvent) => void
}): HTMLButtonElement {
  const { label, variant = 'default', size = 'md', icon, onClick } = options

  const btn = document.createElement('button')
  btn.textContent = icon ? `${icon} ${label}` : label

  const padding = size === 'sm' ? '4px 10px' : '8px 16px'
  const fontSize = size === 'sm' ? 'var(--hs-text-sm)' : 'var(--hs-text-base)'

  Object.assign(btn.style, {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--hs-space-1)',
    padding,
    fontSize,
    fontWeight: 'var(--hs-weight-medium)',
    fontFamily: 'var(--hs-font)',
    lineHeight: '1',
    border: 'none',
    borderRadius: 'var(--hs-radius-pill)',
    cursor: 'pointer',
    transition: `all var(--hs-fade-duration) var(--hs-ease-fast)`,
    outline: 'none',
    whiteSpace: 'nowrap',
  })

  switch (variant) {
    case 'accent':
      Object.assign(btn.style, {
        background: 'var(--hs-accent)',
        color: 'var(--hs-accent-text)',
        boxShadow: `0 2px 12px var(--hs-accent-glow)`,
      })
      btn.addEventListener('mouseenter', () => {
        btn.style.filter = 'brightness(1.12)'
        btn.style.transform = 'scale(1.03)'
      })
      btn.addEventListener('mouseleave', () => {
        btn.style.filter = ''
        btn.style.transform = ''
      })
      break

    case 'ghost':
      Object.assign(btn.style, {
        background: 'transparent',
        color: 'var(--hs-text-secondary)',
      })
      btn.addEventListener('mouseenter', () => {
        btn.style.background = 'var(--hs-glass-bg)'
        btn.style.color = 'var(--hs-text-primary)'
      })
      btn.addEventListener('mouseleave', () => {
        btn.style.background = 'transparent'
        btn.style.color = 'var(--hs-text-secondary)'
      })
      break

    default: // 'default'
      Object.assign(btn.style, {
        background: 'var(--hs-glass-bg-elevated)',
        color: 'var(--hs-text-primary)',
        backdropFilter: `blur(var(--hs-glass-blur)) saturate(var(--hs-glass-saturate))`,
        WebkitBackdropFilter: `blur(var(--hs-glass-blur)) saturate(var(--hs-glass-saturate))`,
        border: '1px solid var(--hs-glass-border)',
      })
      btn.addEventListener('mouseenter', () => {
        btn.style.borderColor = 'var(--hs-glass-border-hover)'
        btn.style.transform = 'scale(1.03)'
      })
      btn.addEventListener('mouseleave', () => {
        btn.style.borderColor = 'var(--hs-glass-border)'
        btn.style.transform = ''
      })
      break
  }

  if (onClick) {
    btn.addEventListener('click', (e) => {
      e.stopPropagation()
      onClick(e)
    })
  }

  return btn
}
